import {BaseAPISvc} from './BaseAPISvc.js';
/** @typedef {import('./Proposal.svc')} Types */
//
export class Proposal extends BaseAPISvc {
	constructor(...args) {
		'ngInject';
		super(...args);
		this.path_v1 = `${this.API}/v1/proposal`;
		this.path_v2 = `${this.API}/v2/proposal`;
	}

	/** @param {Types.GetAllProposalTypesParams} params */
	getAllProposalTypes(params, doCache = true) {
		return this.http.get(`${this.path_v1}/allProposalTypes`, {params: params, catch: doCache});
	}

	/** @param {Types.SaveProposalCheckListAnswersParams} params */
	saveProposalCheckListAnswers(params) {
		return this.http.post(`${this.path_v1}/saveProposalChecklistAnswers`, params);
	}

	/** @param {Types.GetAllProposalTypesByServiceProviderAndTemplateTypeParams} params */
	getAllProposalTypesByServiceProviderAndTemplateType(params) {
		return this.http.get(`${this.path_v1}/allProposalTypesByServiceProviderAndTemplateType`, {params});
	}

	/** @param {Types.GetAllProposalTypeOptionsByServiceProviderAndSystemTypeParams} params */
	getAllProposalTypeOptionsByServiceProviderAndSystemType(params) {
		return this.http.get(`${this.path_v1}/allProposalTypeOptionsByServiceProviderAndSystemType`, {
			params,
		});
	}

	/** @param {Types.GetAllBundlesForProposalParams} params */
	getAllBundlesForProposal(params) {
		return this.http.get(`${this.path_v1}/allBundlesForProposal`, {params});
	}

	/** @param {Types.GetBundlePriceAndAvailabilityForSystemPreferencesParams} params */
	getBundlePriceAndAvailabilityForSystemPreferences({mock, ...params}) {
		if (mock) return this.dbSvc.getBundlePriceAndAvailabilityForSystemPreferences(params);
		return this.http.get(`${this.path_v1}/bundlePriceAndAvailabilityForSystemPreferences`, {params: params});
	}

	/** @param {Types.GetPreviewOfTheProposalsParams} params */
	getPreviewOfTheProposals(params) {
		return this.http.get(`${this.path_v2}/previewOfTheProposals`, {params});
	}

	/** @param {Types.AddSystemToProposalParams} params */
	addSystemToProposal({mock, ...params}) {
		if (mock) return this.dbSvc.addSystemToProposal(params);
		return this.http.post(`${this.path_v1}/addSystemToProposal`, {params: params});
	}

	/** @param {Types.RemoveBundleFromProposalParams} params */
	removeBundleFromProposal(params) {
		return this.http.post(`${this.path_v1}/removeBundleFromProposal`, params);
	}

	/** @param {Types.ReplaceEquipmentInBundleParams} params */
	replaceEquipmentInBundle({mock, ...params}) {
		if (mock) return this.dbSvc.replaceEquipmentInBundle(params);
		Object.assign(params, {equipmentsJson: JSON.stringify(params.equipmentsJson)});
		return this.http.post(`${this.path_v1}/replaceEquipmentInBundle`, params);
	}

	/** @param {Types.UpdateProposalConsultantParams} params */
	updateProposalConsultant(params) {
		return this.http.post(`${this.path_v1}/updateProposalConsultant`, params);
	}

	/** @param {Types.GetProposalStateAndStatusParams} params */
	getProposalStateAndStatus(params) {
		return this.http.get(`${this.path_v1}/proposalStateAndStatus`, {params});
	}

	/** @param {Types.PostSaveProposalStateForCustomerParams} params */
	postSaveProposalStateForCustomer(params) {
		const config = {headers: {Auth: this.PUBLIC_KEY}};
		return this.http.post(`${this.path_v1}/saveProposalStateForCustomer`, params, config);
	}

	/** @param {Types.UpdateProposalStatusToDraftParams} params */
	updateProposalStatusToDraft(params) {
		return this.http.post(`${this.path_v1}/updateProposalStatusToDraft`, params);
	}

	/** @param {Types.GetBundlePricingBreakDownParams} params */
	getBundlePricingBreakDown(params) {
		return this.http.get(`${this.path_v1}/bundlePricingBreakdown`, {params});
	}

	/** @param {Types.GetProposalsParams} params */
	getProposals(params) {
		return this.http.get(`${this.path_v2}/proposals`, {params});
	}

	/** @param {Types.PostCleanPreviousBundlesSelectionParams} params */
	postCleanPreviousBundlesSelection(params) {
		return this.http.post(`${this.path_v1}/cleanPreviousBundlesSelection`, params);
	}

	/** @param {Types.GetBundleSkeletonBySystemTypeParams} params */
	getBundleSkeletonBySystemType(params) {
		return this.http.get(`${this.path_v1}/bundleSkeletonBySystemType`, {params});
	}

	/** @param {Types.GetConsultationHasAccessoriesParams} params */
	getConsultationHasAccessories(params) {
		return this.http.get(`${this.path_v1}/consultationHasAccessories`, params);
	}

	/** @param {Types.GetProposalSnapshotDetailsByEventLogAndPositionParams} params */
	getProposalSnapshotDetailsByEventLogAndPosition(params) {
		return this.http.get(`${this.path_v2}/proposalSnapshotDetailsByEventLogAndPosition`, {params});
	}

	/** @param {Types.GetProviderThemeSettingsParams} params */
	getProviderThemeSettings(params) {
		return this.http.get(`${this.path_v1}/providerThemeSettings`, {params});
	}

	/** @param {Types.PostSaveSystemNameParams} params */
	postSaveSystemName(params) {
		return this.http.post(`${this.path_v1}/saveSystemName`, params);
	}

	/** @param {Types.PostUpdateDepositParams} params */
	postUpdateDeposit(params) {
		return this.http.post(`${this.path_v2}/updateDeposit`, params);
	}

	/** @param {Types.PostSaveFinancingOptionSelectedParams} params */
	postSaveFinancingOptionSelected(params) {
		return this.http.post(`${this.path_v2}/saveFinancingOptionSelected`, params);
	}

	/** @param {Types.GetOverridePriceDataParams} params */
	getOverridePriceData(params) {
		return this.http.get(`${this.path_v2}/overriddenPriceData`, {params});
	}

	/** @param {Types.PostOverrideProposalsPriceParams} params */
	postOverrideProposalsPrice(params) {
		return this.http.post(`${this.path_v2}/overrideProposalsPrice`, params);
	}

	/** @param {Types.PostResetOverriddenProposalPricesParams} params */
	postResetOverriddenProposalPrices(params) {
		return this.http.post(`${this.path_v2}/resetOverriddenProposalPrices`, params);
	}

	/** @param {Types.GetAllBundlesForPricingBookParams} params */
	getAllBundlesForPricingBook({mock, ...params}, cache = false) {
		if (mock) return this.dbSvc.getAllBundlesForPricingBook(params);
		return this.http.get(`${this.path_v1}/allBundlesForPricingBook`, {params: params, cache});
	}

	/** @param {Types.GetBundlePriceForPricingBookParams} params */
	getBundlePriceForPricingBook({mock, ...params}, cache = false) {
		if (mock) return this.dbSvc.getBundlePriceForPricingBook(params, cache);
		return this.http.get(`${this.path_v1}/bundlePriceForPricingBook`, {params: params, cache});
	}

	/** @param {Types.PostUpdateLabelParams} params */
	postUpdateLabel(params) {
		return this.http.post(`${this.path_v2}/updateLabel`, params);
	}

	/** @param {Types.PostUpdateTaxRateParams} params */
	postUpdateTaxRate(params) {
		return this.http.post(`${this.path_v2}/updateTaxRate`, params);
	}

	/** @param {Types.PostAddNotesParams} params */
	postAddNotes(params) {
		return this.http.post(`${this.path_v2}/addNotes`, params);
	}

	/** @param {Types.PostRemoveNoteParams} params */
	postRemoveNote(params) {
		return this.http.post(`${this.path_v2}/removeNotes`, params);
	}

	/** @param {Types.GetNotesPreviewParams} params */
	getNotesPreview(params) {
		return this.http.get(`${this.path_v2}/notesPreview`, {params});
	}

	/** @param {Types.GetNoteDetailsParams} params */
	getNoteDetails(params) {
		return this.http.get(`${this.path_v2}/noteDetails`, {params});
	}

	/** @param {Types.GetNoteDetailsForCustomerParams} params */
	getNoteDetailsForCustomer(params) {
		const headers = {Auth: this.PUBLIC_KEY};
		return this.http.get(`${this.path_v2}/noteDetailsForCustomer`, {params, headers});
	}

	/** @param {Types.GetPublicNotesForSnapshotParams} params */
	getPublicNotesForSnapshot(params) {
		return this.http.get(`${this.path_v2}/noteDetailsFromSnapshot`, {params});
	}

	/** @param {Types.GetAISummaryDetailsParams} params */
	getAISummaryDetails(params) {
		return this.http.get(`${this.path_v2}/aiSummaryDetails`, {params});
	}

	/** @param {Types.GetAISummaryDetailsForCustomerParams} params */
	getAISummaryDetailsForCustomer(params) {
		const headers = {Auth: this.PUBLIC_KEY};
		return this.http.get(`${this.path_v2}/aiSummaryDetailsForCustomer`, {params, headers});
	}

	/** @param {Types.GetAISummaryFromSnapshotParams} params */
	getAISummaryFromSnapshot(params) {
		return this.http.get(`${this.path_v2}/aiSummaryFromSnapshot`, {params});
	}

	/** @param {Types.SaveSecondaryFinancingOptionSelectedParams} params */
	saveSecondaryFinancingOptionSelected(params) {
		return this.http.post(`${this.path_v2}/saveSecondaryFinancingOptionSelected`, params);
	}

	/** @param {Types.PostSaveSecondaryFinancingOptionSelectedForCustomerParams} params */
	postSaveSecondaryFinancingOptionSelectedForCustomer(params) {
		const config = {headers: {Auth: this.PUBLIC_KEY}};
		return this.http.post(`${this.path_v2}/saveSecondaryFinancingOptionSelectedForCustomer`, params, config);
	}
}
//
let compMod = angular.module('mod.svc.proposalService', []).service('ProposalService', Proposal);
export default (compMod = compMod.name);
